import React, { useEffect, useRef, useState } from 'react'
import { usePhotoSlotsStore }                  from '../store/usePhotoSlotsStore'
import { SlotIcon }                            from '../utils/constants'
import type { PhotoSlot }                      from '../utils/constants'
import { SchemaPositionEditor }                from '../components/SchemaPositionEditor'

const EMOJI_PALETTE = [
  '🚗','🚙','🚕','🚌','🚎','🏎️','🚓','🚑','🚒','🛻',
  '⬆️','⬇️','➡️','⬅️','↗️','↘️','↙️','↖️','🔧','📸',
  '🪟','💺','🛞','⚠️',
]

function slugify(str: string) {
  return str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}

export function PhotoSlotsManager() {
  const {
    photoSlots,
    slotsLoading,
    slotsError,
    fetchSlots,
    addSlot,
    updateSlot,
    removeSlot,
    reorderSlots,
  } = usePhotoSlotsStore()

  const [editingId,      setEditingId]      = useState<string | null>(null)
  const [editLabel,      setEditLabel]      = useState('')
  const [editIcon,       setEditIcon]       = useState('')
  const [showPalette,    setShowPalette]    = useState<string | null>(null)

  const [newLabel,       setNewLabel]       = useState('')
  const [newIcon,        setNewIcon]        = useState('📸')
  const [showNewPalette, setShowNewPalette] = useState(false)

  const [confirmDelete,  setConfirmDelete]  = useState<string | null>(null)
  
  const [viewMode,       setViewMode]       = useState<'list' | 'schema'>('list')

  const dragItem     = useRef<number | null>(null)
  const dragOverItem = useRef<number | null>(null)

  useEffect(() => { fetchSlots() }, [fetchSlots])

  /* ── Drag & Drop ──────────────────────────────────────────────────────── */
  const handleDragStart = (index: number) => { dragItem.current = index }
  const handleDragEnter = (index: number) => { dragOverItem.current = index }
  const handleDragEnd   = () => {
    if (dragItem.current === null || dragOverItem.current === null) return
    const reordered = [...photoSlots]
    const [moved]   = reordered.splice(dragItem.current, 1)
    reordered.splice(dragOverItem.current, 0, moved)
    dragItem.current     = null
    dragOverItem.current = null
    reorderSlots(reordered)
  }

  /* ── Édition inline ───────────────────────────────────────────────────── */
  const startEdit = (slot: PhotoSlot) => {
    setEditingId(slot.id)
    setEditLabel(slot.label)
    setEditIcon(slot.icon ?? '')   // ✅ Fix : null → ''
    setShowPalette(null)
  }
  const saveEdit = () => {
    if (!editingId) return
    updateSlot(editingId, { label: editLabel, icon: editIcon || undefined })
    setEditingId(null)
    setShowPalette(null)
  }

  /* ── Ajout ────────────────────────────────────────────────────────────── */
  const handleAdd = () => {
    if (!newLabel.trim()) return
    addSlot({ id: slugify(newLabel), label: newLabel.trim(), icon: newIcon })
    setNewLabel('')
    setNewIcon('📸')
  }

  /* ── Helper : affichage icône dans la liste ───────────────────────────── */
  const renderSlotIcon = (slot: PhotoSlot) => {
    // SVG custom (bench, cargo, bench+cargo)
    if (slot.svgIcon) {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <SlotIcon slot={slot} color="#a5b4fc" size={28} />
        </span>
      )
    }
    // Emoji
    if (slot.icon) return <span style={{ fontSize: 22 }}>{slot.icon}</span>
    // Fallback
    return <span style={{ fontSize: 22, opacity: 0.3 }}>📷</span>
  }

  /* ── Rendu ────────────────────────────────────────────────────────────── */
  return (
    <div style={{ fontFamily: 'sans-serif', maxWidth: 640, margin: '0 auto', padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>📸 Gestion des zones photo</h2>

      {/* ── États loading / erreur ── */}
      {slotsLoading && (
        <p style={{ color: '#a5b4fc', fontSize: 13 }}>Chargement…</p>
      )}
      {slotsError && !slotsLoading && (
        <p style={{ color: '#f87171', fontSize: 13 }}>
          ⚠️ Erreur : {slotsError} — slots par défaut utilisés
        </p>
      )}

      {/* ── Tabs List/Schema ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setViewMode('list')}
          style={{
            flex: 1, padding: '8px', borderRadius: '8px', fontWeight: 'bold', border: 'none', cursor: 'pointer',
            background: viewMode === 'list' ? '#4f46e5' : '#2a2a3e',
            color: viewMode === 'list' ? '#fff' : '#aaa'
          }}
        >
          📄 Liste
        </button>
        <button
          onClick={() => setViewMode('schema')}
          style={{
            flex: 1, padding: '8px', borderRadius: '8px', fontWeight: 'bold', border: 'none', cursor: 'pointer',
            background: viewMode === 'schema' ? '#4f46e5' : '#2a2a3e',
            color: viewMode === 'schema' ? '#fff' : '#aaa'
          }}
        >
          🚗 Éditeur Schéma
        </button>
      </div>

      {viewMode === 'schema' ? (
        <SchemaPositionEditor slots={photoSlots} onSaveSlot={updateSlot} />
      ) : (
        <>
          {/* ── Liste des slots ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {photoSlots.map((slot, index) => (
          <div
            key={slot.id}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragEnter={() => handleDragEnter(index)}
            onDragEnd={handleDragEnd}
            onDragOver={e => e.preventDefault()}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: '#1e1e2e', borderRadius: 10, padding: '10px 14px',
              cursor: 'grab', border: '1px solid #333',
            }}
          >
            {/* Poignée drag */}
            <span style={{ color: '#666', fontSize: 18, cursor: 'grab' }}>⠿</span>

            {/* Icône — édition ou affichage */}
            {editingId === slot.id ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowPalette(showPalette === slot.id ? null : slot.id)}
                  style={{
                    fontSize: 22, background: 'none', border: '1px solid #555',
                    borderRadius: 6, padding: '2px 6px', cursor: 'pointer',
                  }}
                >
                  {/* En édition : si svgIcon, on montre un placeholder emoji */}
                  {slot.svgIcon && !editIcon ? '🖼️' : (editIcon || '📷')}
                </button>
                {showPalette === slot.id && (
                  <div style={{
                    position: 'absolute', top: 36, left: 0, zIndex: 100,
                    background: '#2a2a3e', border: '1px solid #555', borderRadius: 8,
                    padding: 8, display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 4,
                  }}>
                    {EMOJI_PALETTE.map(e => (
                      <button key={e}
                        onClick={() => { setEditIcon(e); setShowPalette(null) }}
                        style={{
                          fontSize: 20, background: 'none', border: 'none',
                          cursor: 'pointer', borderRadius: 4, padding: 2,
                        }}
                      >{e}</button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              renderSlotIcon(slot)   // ✅ Fix : gère emoji + SVG + fallback
            )}

            {/* Label */}
            {editingId === slot.id ? (
              <input
                value={editLabel}
                onChange={e => setEditLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveEdit()}
                autoFocus
                style={{
                  flex: 1, background: '#111', color: '#fff',
                  border: '1px solid #555', borderRadius: 6,
                  padding: '4px 8px', fontSize: 14,
                }}
              />
            ) : (
              <span style={{ flex: 1, color: '#fff', fontSize: 14 }}>{slot.label}</span>
            )}

            {/* Numéro d'ordre */}
            <span style={{ color: '#555', fontSize: 11, minWidth: 20, textAlign: 'right' }}>
              #{index + 1}
            </span>

            {/* Actions */}
            {editingId === slot.id ? (
              <>
                <button onClick={saveEdit}
                  style={{
                    background: '#4f46e5', color: '#fff', border: 'none',
                    borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                  }}>✓</button>
                <button onClick={() => setEditingId(null)}
                  style={{
                    background: '#333', color: '#fff', border: 'none',
                    borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                  }}>✕</button>
              </>
            ) : (
              <>
                <button onClick={() => startEdit(slot)}
                  style={{
                    background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: 16, color: '#aaa',
                  }}>✏️</button>
                {confirmDelete === slot.id ? (
                  <>
                    <button
                      onClick={() => { removeSlot(slot.id); setConfirmDelete(null) }}
                      style={{
                        background: '#dc2626', color: '#fff', border: 'none',
                        borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12,
                      }}>Confirmer</button>
                    <button onClick={() => setConfirmDelete(null)}
                      style={{
                        background: '#333', color: '#fff', border: 'none',
                        borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12,
                      }}>Annuler</button>
                  </>
                ) : (
                  <button onClick={() => setConfirmDelete(slot.id)}
                    style={{
                      background: 'none', border: 'none',
                      cursor: 'pointer', fontSize: 16, color: '#f87171',
                    }}>🗑️</button>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* ── Formulaire ajout ── */}
      <div style={{
        marginTop: 24, background: '#1e1e2e', borderRadius: 10,
        padding: 16, border: '1px dashed #4f46e5',
      }}>
        <h3 style={{ color: '#a5b4fc', marginBottom: 12, fontSize: 14 }}>➕ Ajouter une zone</h3>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>

          {/* Icône */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowNewPalette(!showNewPalette)}
              style={{
                fontSize: 22, background: '#111', border: '1px solid #555',
                borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
              }}
            >{newIcon}</button>
            {showNewPalette && (
              <div style={{
                position: 'absolute', bottom: 40, left: 0, zIndex: 100,
                background: '#2a2a3e', border: '1px solid #555', borderRadius: 8,
                padding: 8, display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 4,
              }}>
                {EMOJI_PALETTE.map(e => (
                  <button key={e}
                    onClick={() => { setNewIcon(e); setShowNewPalette(false) }}
                    style={{
                      fontSize: 20, background: 'none', border: 'none',
                      cursor: 'pointer', borderRadius: 4, padding: 2,
                    }}
                  >{e}</button>
                ))}
              </div>
            )}
          </div>

          {/* Nom */}
          <input
            placeholder="Nom de la zone (ex: Toit)"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            style={{
              flex: 1, background: '#111', color: '#fff',
              border: '1px solid #555', borderRadius: 6,
              padding: '6px 10px', fontSize: 14,
            }}
          />

            <button onClick={handleAdd}
              style={{
                background: '#4f46e5', color: '#fff', border: 'none',
                borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 600,
              }}>Ajouter</button>
          </div>
        </div>
        </>
      )}
    </div>
  )
}